import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function normalizeExpression(raw) {
  let s = typeof raw === "string" ? raw : "";
  s = s.replace(/^\s*f'?\(x\)\s*=\s*/i, "");
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = s.replace(/\s+/g, "");
  s = explicitMultiply(s);
  s = s.replace(/\^/g, "**");
  return s;
}

function safeEval(expr, x) {
  const normalized = normalizeExpression(expr);
  if (!normalized) return NaN;
  const js = normalized.replace(/x/g, `(${x})`);
  try {
    const value = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(value) ? value : NaN;
  } catch {
    return NaN;
  }
}

function areEquivalent(userExpr, expectedExpr) {
  const points = [-3, -2, -1, 0, 1, 2, 3];
  for (const x of points) {
    const userVal = safeEval(userExpr, x);
    const expectedVal = safeEval(expectedExpr, x);
    if (!Number.isFinite(userVal) || !Number.isFinite(expectedVal)) return false;
    if (Math.abs(userVal - expectedVal) > 0.001) return false;
  }
  return true;
}

function termLatex(coef, power, isFirst = false) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const signPart = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${signPart}${abs}`;
  if (power === 1) return `${signPart}${abs === 1 ? "x" : `${abs}x`}`;
  return `${signPart}${abs === 1 ? `x^{${power}}` : `${abs}x^{${power}}`}`;
}

function polyLatex(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";
  return nonZero.map((t, idx) => termLatex(t.coef, t.power, idx === 0)).join("");
}

function termAscii(coef, power, isFirst = false) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const prefix = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${prefix}${abs}`;
  if (power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
  return `${prefix}${abs === 1 ? `x^${power}` : `${abs}x^${power}`}`;
}

function polyAscii(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";
  return nonZero.map((t, idx) => termAscii(t.coef, t.power, idx === 0)).join("");
}

function derivative(terms) {
  return terms
    .filter((t) => t.power !== 0)
    .map((t) => ({ coef: t.coef * t.power, power: t.power - 1 }))
    .filter((t) => t.coef !== 0)
    .sort((a, b) => b.power - a.power);
}

function multiplyPolynomials(aTerms, bTerms) {
  const acc = new Map();
  for (const a of aTerms) {
    for (const b of bTerms) {
      const power = a.power + b.power;
      const coef = a.coef * b.coef;
      acc.set(power, (acc.get(power) || 0) + coef);
    }
  }
  return Array.from(acc.entries())
    .map(([power, coef]) => ({ power: Number(power), coef }))
    .filter((t) => t.coef !== 0)
    .sort((x, y) => y.power - x.power);
}

function buildFactor(termCount, maxDegree) {
  const powers = [];
  while (powers.length < termCount) {
    const power = randInt(0, maxDegree);
    if (!powers.includes(power)) powers.push(power);
  }
  powers.sort((a, b) => b - a);
  return powers.map((power, idx) => ({
    coef: randInt(-5, 5, idx === 0 ? [0] : [0]),
    power,
  }));
}

export function generateTask() {
  const leftCount = Math.random() < 0.5 ? 2 : 3;
  const rightCount = 2;
  const leftTerms = buildFactor(leftCount, leftCount === 3 ? 2 : 2);
  const rightTerms = buildFactor(rightCount, 2);
  const leftDerivative = derivative(leftTerms);
  const rightDerivative = derivative(rightTerms);
  const expandedTerms = multiplyPolynomials(leftTerms, rightTerms);
  const expandedDerivative = derivative(expandedTerms);

  const leftLatex = polyLatex(leftTerms);
  const rightLatex = polyLatex(rightTerms);
  const leftDerivativeLatex = polyLatex(leftDerivative);
  const rightDerivativeLatex = polyLatex(rightDerivative);
  const expandedLatex = polyLatex(expandedTerms);
  const derivativeLatex = polyLatex(expandedDerivative);
  const derivativeAscii = polyAscii(expandedDerivative);

  return {
    type: "ProductDerivativeTask",
    prompt: `Oblicz pochodna iloczynu funkcji: f(x) = (${leftLatex})(${rightLatex}). Podaj f'(x).`,
    answer: derivativeAscii,
    data: {
      leftLatex,
      rightLatex,
      leftDerivativeLatex,
      rightDerivativeLatex,
      expandedLatex,
      derivativeLatex,
      derivativeAscii,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna iloczynu funkcji:</p>
      <BlockMath math={`f(x) = (${data.leftLatex})(${data.rightLatex})`} />
      <p>Podaj <InlineMath math={"f'(x)"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math={"f'(x) ="} /></div>
      <EquationInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 6x^2+4x-1"
        basis="clamp(220px, 34ch, 340px)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) return false;
  return areEquivalent(userAnswer, data.derivativeAscii);
}

export function renderUserAnswer(userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f'(x) = ${userAnswer.replace(/\*/g, "\\cdot ")}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = ${data.derivativeLatex}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru <InlineMath math={"(f(x) \\cdot g(x))' = f'(x) \\cdot g(x) + f(x) \\cdot g'(x)"} />.
      </p>
      <BlockMath math={`f(x) = (${data.leftLatex})(${data.rightLatex})`} />
      <p><strong>Krok 2:</strong> Liczymy pochodne obu czynnikow:</p>
      <BlockMath math={`f_1(x) = ${data.leftLatex},\\quad f_1'(x) = ${data.leftDerivativeLatex}`} />
      <BlockMath math={`f_2(x) = ${data.rightLatex},\\quad f_2'(x) = ${data.rightDerivativeLatex}`} />
      <p><strong>Krok 3:</strong> Podstawiamy do wzoru:</p>
      <BlockMath math={`f'(x) = (${data.leftDerivativeLatex})(${data.rightLatex}) + (${data.leftLatex})(${data.rightDerivativeLatex})`} />
      <p><strong>Krok 4:</strong> Dla kontroli mozemy wymnozyc iloczyn:</p>
      <BlockMath math={`(${data.leftLatex})(${data.rightLatex}) = ${data.expandedLatex}`} />
      <p><strong>Krok 5:</strong> Liczymy pochodna z wymnozonej postaci i dostajemy ten sam wynik:</p>
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
    </div>
  );
}

